import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
  Typography,
  Box,
  TextField,
  Button,
  Link,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuthStore } from '../../store';
import type { LoginCredentials } from '../../types/auth';

interface LocationState {
  from?: { pathname: string };
}

// Shared text field styling for the redesigned login theme
const textFieldSx = {
  mb: 3,
  '& .MuiOutlinedInput-root': {
    bgcolor: 'rgba(20, 40, 100, 0.5)',
    transition: 'all 300ms ease',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.2)',
      transition: 'border-color 300ms ease',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.5)',
      borderWidth: 2,
    },
  },
  '& .MuiOutlinedInput-input': {
    color: '#ffffff',
    py: 1.75,
    px: 1.75,
    fontSize: '0.95rem',
    '&::placeholder': {
      color: 'rgba(255, 255, 255, 0.5)',
      opacity: 1,
    },
  },
  '& .MuiInputBase-root': {
    borderRadius: '8px',
  },
  '& .MuiFormHelperText-root': {
    color: '#ff6b6b',
    fontSize: '0.8rem',
  },
};

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);

  const { login, isLoading, error, clearError } = useAuthStore();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginCredentials>({
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginCredentials) => {
    clearError();
    const success = await login(data);

    if (success) {
      const user = useAuthStore.getState().user;
      if (!user?.guideAcknowledged) {
        navigate('/guide', { replace: true });
        return;
      }

      // Redirect to the page they tried to visit, or dashboard
      const state = location.state as LocationState;
      const from = state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100vw',
        height: '100vh',
        position: 'relative',
        overflow: 'hidden',
        backgroundImage: 'url(/images/molecules-bg.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}
    >
      {/* Overlay - Blur and Dim Effect */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.5)',
          backdropFilter: 'blur(8px)',
          zIndex: 0,
        }}
      />

      {/* Outer Frame - Max width & height container, centered */}
      <Box
        sx={{
          display: 'flex',
          width: { xs: '100%', md: 1600 },
          height: { xs: '100%', md: 900 },
          maxWidth: '100%',
          maxHeight: '100vh',
          position: 'relative',
          zIndex: 1,
          m: 'auto',
          borderRadius: { xs: 0, md: 2 },
          boxShadow: { xs: 'none', md: '0 20px 60px rgba(0, 0, 0, 0.6)' },
          overflow: 'hidden',
        }}
      >
        {/* Left Panel - Branding */}
        <Box
          sx={{
            flex: 1,
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'flex-start',
            p: 8,
            background: 'linear-gradient(135deg, rgba(30, 58, 138, 0.4) 0%, rgba(59, 130, 246, 0.3) 100%)',
            backdropFilter: 'blur(10px)',
            borderRight: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          {/* Logo */}
          <Box sx={{ mb: 6 }}>
            <Box
              component="img"
              src="/ediphor-logo-light.svg"
              alt="Ediphor"
              sx={{
                height: 56,
                transition: 'filter 300ms ease',
                filter: 'drop-shadow(0 4px 12px rgba(59, 130, 246, 0.2))',
              }}
            />
          </Box>

          {/* LabCloud Title - Emphasized */}
          <Typography
            variant="h1"
            sx={{
              color: '#ffffff',
              fontWeight: 700,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              lineHeight: 1.2,
              letterSpacing: -1,
              background: 'linear-gradient(135deg, #ffffff 0%, #e0f2fe 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: 'none',
            }}
          >
            LabCloud
          </Typography>

          {/* Subtitle - App Purpose */}
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '0.95rem',
              mt: 3,
              fontWeight: 300,
              letterSpacing: 0.5,
              maxWidth: 300,
            }}
          >
            Diagnostic testing platform
          </Typography>
        </Box>

        {/* Vertical Divider */}
        <Box
          sx={{
            width: '1px',
            background: 'linear-gradient(to bottom, transparent, rgba(255, 255, 255, 0.2), transparent)',
            display: { xs: 'none', md: 'block' },
          }}
        />

        {/* Right Panel - Login Form */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            width: { xs: '100%', md: '50%' },
            p: { xs: 4, md: 8 },
            background: 'linear-gradient(135deg, rgba(30, 58, 138, 0.35) 0%, rgba(59, 130, 246, 0.25) 100%)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: 380,
            }}
          >
            {/* Sign in Header */}
            <Typography
              variant="h4"
              sx={{
                color: '#ffffff',
                fontWeight: 500,
                fontSize: '1.5rem',
                mb: 6,
                letterSpacing: 0.3,
              }}
            >
              Sign in to your account
            </Typography>

            {/* Error Alert */}
            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  bgcolor: 'rgba(255, 107, 107, 0.15)',
                  color: '#ff8787',
                  '& .MuiAlert-icon': {
                    color: '#ff6b6b',
                  },
                }}
                onClose={clearError}
              >
                {error}
              </Alert>
            )}

            {/* Login Form */}
            <Box
              component="form"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
            >
              {/* Email Field */}
              <Controller
                name="email"
                control={control}
                rules={{
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    placeholder="Email address"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    disabled={isLoading}
                    sx={textFieldSx}
                  />
                )}
              />

              {/* Password Field */}
              <Controller
                name="password"
                control={control}
                rules={{
                  required: 'Password is required',
                  minLength: {
                    value: 6,
                    message: 'Password must be at least 6 characters',
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    placeholder="Password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    disabled={isLoading}
                    sx={textFieldSx}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleTogglePasswordVisibility}
                              edge="end"
                              disabled={isLoading}
                              sx={{ color: 'rgba(20, 40, 100, 0.8)', transition: 'all 200ms ease', '&:hover': { color: 'rgba(20, 40, 100, 1)' } }}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />

              {/* Forgot Password Link */}
              <Box sx={{ textAlign: 'right', mb: 4 }}>
                <Link
                  href="#"
                  underline="none"
                  sx={{
                    color: '#ffffff',
                    fontSize: '0.875rem',
                    transition: 'all 300ms ease',
                    position: 'relative',
                    opacity: 0.9,
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -2,
                      left: 0,
                      width: 0,
                      height: '1px',
                      bgcolor: '#ffffff',
                      transition: 'width 300ms ease',
                    },
                    '&:hover': {
                      opacity: 1,
                    },
                    '&:hover::after': {
                      width: '100%',
                    },
                  }}
                >
                  Forgot password?
                </Link>
              </Box>

              {/* Sign In Button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                  color: '#1e3a8a',
                  py: 1.8,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '10px',
                  transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
                  '&:hover': {
                    bgcolor: '#ffffff',
                    boxShadow: '0 8px 24px rgba(59, 130, 246, 0.4)',
                    transform: 'translateY(-2px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                  '&.Mui-disabled': {
                    bgcolor: 'rgba(255, 255, 255, 0.3)',
                    color: 'rgba(255, 255, 255, 0.5)',
                    boxShadow: 'none',
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} sx={{ color: '#1e3a8a' }} />
                ) : (
                  'Sign in'
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
